import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  User, LogOut, Cloud, CloudOff, RefreshCw, LogIn, 
  ChevronDown, Check, Loader2, Crown, CreditCard, Trash2 
} from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '../contexts/SubscriptionContext';
import PlanSelectionModal from './PlanSelectionModal';
import DeleteAccountDialog from './DeleteAccountDialog';

const UserMenu = () => {
  const { user, isAuthenticated, logout, syncPortfolioData, loadPortfolioData } = useAuth();
  const { isPremium, entitlement, openPortal } = useSubscription();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReauthed, setDeleteReauthed] = useState(false);
  const location = useLocation();

  // Resume the deletion dialog right after a Google re-authentication round-trip
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('delete_account') === '1' && isAuthenticated) {
      setDeleteReauthed(true);
      setDeleteOpen(true);
      params.delete('delete_account');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search, isAuthenticated]);

  const openDeleteDialog = () => { setDeleteReauthed(false); setDeleteOpen(true); };

  const handleManageBilling = async () => {
    try {
      await openPortal();
    } catch (e) {
      toast.error('Could not open billing portal');
    }
  };

  const renewal = entitlement?.current_period_end
    ? new Date(entitlement.current_period_end).toLocaleDateString()
    : null;

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncPortfolioData();
      toast.success('Portfolio data synced to cloud!');
    } catch (error) {
      toast.error('Failed to sync data');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLoad = async () => {
    setIsLoading(true);
    try {
      await loadPortfolioData();
      toast.success('Portfolio data loaded from cloud!');
      // Reload the page to reflect new data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
  };

  if (!isAuthenticated) {
    return (
      <Button
        onClick={() => navigate('/login')}
        className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 font-semibold shadow-lg shadow-amber-500/25 border border-amber-300/30"
        data-testid="sign-in-btn"
      >
        <LogIn className="h-4 w-4 mr-2" />
        Sign In
      </Button>
    );
  }

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          data-testid="user-menu-trigger"
        >
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="h-6 w-6 rounded-full mr-2"
            />
          ) : (
            <User className="h-4 w-4 mr-2" />
          )}
          <span className="max-w-[100px] truncate">{user?.name || user?.email}</span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user?.name}</p>
          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />

        {/* Subscription status */}
        {isPremium ? (
          <>
            <div className="px-2 py-1.5" data-testid="subscription-status">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                <Crown className="h-3.5 w-3.5" />
                {entitlement?.status === 'comp' ? 'Comp Access' : 'Premium Active'}
              </div>
              {entitlement?.plan_type && entitlement?.plan_type !== 'comp' && (
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  {entitlement.plan_type} plan
                  {renewal && (entitlement?.cancel_at_period_end
                    ? ` · ends ${renewal}`
                    : ` · renews ${renewal}`)}
                </p>
              )}
            </div>
            {entitlement?.status !== 'comp' && (
              <DropdownMenuItem onClick={handleManageBilling} className="cursor-pointer" data-testid="manage-subscription-btn">
                <CreditCard className="h-4 w-4 mr-2 text-indigo-500" />
                Manage Subscription
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={() => setPlansOpen(true)} className="cursor-pointer" data-testid="upgrade-premium-btn">
              <Crown className="h-4 w-4 mr-2 text-amber-500" />
              Upgrade to Premium
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem 
          onClick={handleSync}
          disabled={isSyncing}
          className="cursor-pointer"
        >
          {isSyncing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Cloud className="h-4 w-4 mr-2 text-blue-500" />
          )}
          Sync to Cloud
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={handleLoad}
          disabled={isLoading}
          className="cursor-pointer"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2 text-green-500" />
          )}
          Load from Cloud
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={handleLogout}
          className="cursor-pointer text-red-600"
          data-testid="sign-out-btn"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
        {user?.auth_method !== 'guest' && (
          <DropdownMenuItem
            onClick={openDeleteDialog}
            className="cursor-pointer text-red-600"
            data-testid="delete-account-menu-item"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Account
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    <PlanSelectionModal open={plansOpen} onOpenChange={setPlansOpen} />
    <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} reauthed={deleteReauthed} />
    </>
  );
};

export default UserMenu;
